import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Quote } from '../pages/customer-quote-review-portal/types';

// Use local logo to avoid CORS issues
const EVOQIA_LOGO_PATH = '/assets/images/evoqia_logo_nb-1765449001818.png';

// Evoqia Brand Colors (RGB values converted from hex)
const PRIMARY_TEXT = [11, 34, 73] as [number, number, number]; // #0B2249 - Primary text color
const SECONDARY_COLOR = [124, 195, 99] as [number, number, number]; // #7CC363 - Only for main "QUOTATION" title
const BACKGROUND_COLOR = [241, 242, 244] as [number, number, number]; // #F1F2F4 - Backgrounds, headers
const TEXT_LIGHT = [100, 116, 139] as [number, number, number]; // For subtle text

// Helper function to load local image
const loadLocalImage = (path: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      // Convert image to data URL
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } else {
        reject(new Error('Failed to get canvas context'));
      }
    };
    
    img.onerror = () => reject(new Error(`Failed to load local image from ${path}`));
    
    // Load from local path
    img.src = path;
    
    // Timeout after 5 seconds
    setTimeout(() => reject(new Error('Image load timeout')), 5000);
  });
};

// Helper function to calculate logo dimensions maintaining aspect ratio
const calculateLogoDimensions = (img: HTMLImageElement, maxWidth: number = 60, maxHeight: number = 20): { width: number; height: number } => {
  const aspectRatio = img.width / img.height;
  
  let width = maxWidth;
  let height = width / aspectRatio;
  
  // If height exceeds max, recalculate based on height
  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }
  
  return { width, height };
};

// Helper function to format currency: no decimals, with thousand separator
const formatCurrency = (amount: number): string => {
  return `€${Math.round(amount).toLocaleString('nl-NL')}`;
};

export const generateQuotePDF = async (quote: Quote): Promise<void> => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;

    // Detect single-section mode (only licenses OR only implementation, not both)
    const hasLicenses = quote.licenses.included && quote.licenses.items.length > 0;
    const hasImplementation = quote.implementation.included && quote.implementation.items.length > 0;
    const singleSection = (hasLicenses && !hasImplementation) || (!hasLicenses && hasImplementation);

    // Compact spacing values for single-section (single A4 page) mode
    const sp = singleSection
      ? {
          titleY: 38,
          titleFontSize: 20,
          refSpacing: 8,
          dateSpacing: 5,
          validSpacing: 5,
          customerGap: 12,
          customerBarH: 7,
          customerBarFontSize: 10,
          customerBarTextOffset: 4.8,
          customerBarSpacing: 9,
          blankAbove: 4,
          nameSpacing: 5,
          contactSpacing: 5,
          addressSpacing: 5,
          blankBelow: 4,
          sectionGap: 4,
          sectionBarH: 7,
          sectionBarFontSize: 10,
          sectionBarTextOffset: 4.8,
          sectionBarSpacing: 8,
          cellPadding: 2,
          tableFontSize: 8,
          tableHeadFontSize: 9,
          afterTableGap: 6,
          totalBarH: 7,
          totalBarFontSize: 10,
          totalBarTextOffset: 4.8,
          totalTopGap: 12,
          totalLineSpacing: 6,
          totalDividerSpacing: 7,
          totalY1Offset: 3,
          notesGap: 10,
          notesBarH: 7,
          notesBarFontSize: 10,
          notesBarTextOffset: 4.8,
          notesBarSpacing: 9,
          notesFontSize: 8,
        }
      : {
          titleY: 42,
          titleFontSize: 24,
          refSpacing: 10,
          dateSpacing: 5,
          validSpacing: 5,
          customerGap: 15,
          customerBarH: 8,
          customerBarFontSize: 11,
          customerBarTextOffset: 5.5,
          customerBarSpacing: 11,
          blankAbove: 5,
          nameSpacing: 6,
          contactSpacing: 6,
          addressSpacing: 6,
          blankBelow: 5,
          sectionGap: 5,
          sectionBarH: 8,
          sectionBarFontSize: 11,
          sectionBarTextOffset: 5.5,
          sectionBarSpacing: 9,
          cellPadding: 3,
          tableFontSize: 9,
          tableHeadFontSize: 10,
          afterTableGap: 8,
          totalBarH: 8,
          totalBarFontSize: 11,
          totalBarTextOffset: 5.5,
          totalTopGap: 14,
          totalLineSpacing: 7,
          totalDividerSpacing: 9,
          totalY1Offset: 4,
          notesGap: 12,
          notesBarH: 8,
          notesBarFontSize: 11,
          notesBarTextOffset: 5.5,
          notesBarSpacing: 11,
          notesFontSize: 9,
        };

    // Load local logo before generating PDF content
    let logoDataUrl: string | null = null;
    let logoDimensions = { width: 50, height: 14 }; // Default dimensions
    
    try {
      logoDataUrl = await loadLocalImage(EVOQIA_LOGO_PATH);
      
      // Calculate proper logo dimensions maintaining aspect ratio
      const logoImg = new Image();
      logoImg.src = logoDataUrl;
      await new Promise((resolve) => {
        logoImg.onload = resolve;
      });
      logoDimensions = calculateLogoDimensions(logoImg);
      
      console.log('Local logo loaded successfully for PDF with dimensions:', logoDimensions);
    } catch (error) {
      console.error('Failed to load local logo for PDF:', error);
      // PDF will generate without logo if it fails
    }

    // Helper function to add header with logo
    const addHeader = (pageNumber: number = 1) => {
      // Add logo if loaded successfully with proper aspect ratio
      if (logoDataUrl) {
        try {
          doc.addImage(
            logoDataUrl, 
            'PNG', 
            margin, 
            margin - 2, 
            logoDimensions.width, 
            logoDimensions.height
          );
          console.log('Logo added to PDF page', pageNumber, 'with dimensions:', logoDimensions);
        } catch (error) {
          console.error('Failed to add logo to PDF page:', error);
        }
      } else {
        console.warn('Logo image not available for page', pageNumber);
      }

      // Add page number with Evoqia colors
      doc.setFontSize(8);
      doc.setTextColor(...TEXT_LIGHT);
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${pageNumber}`, pageWidth - margin, margin, { align: 'right' });
    };

    // Add header
    addHeader();

    // Header info block - all in small black font (no bold green title)
    let yPos = sp.titleY;
    doc.setFontSize(10);
    doc.setTextColor(...PRIMARY_TEXT);
    doc.setFont('helvetica', 'normal');
    doc.text(`Project Name: ${quote.projectName || ''}`, margin, yPos);

    yPos += sp.refSpacing;
    doc.text(`Reference: ${quote.referenceCode}`, margin, yPos);

    yPos += sp.dateSpacing;
    doc.text(`Date: ${new Date(quote.createdDate).toLocaleDateString('en-GB')}`, margin, yPos);

    yPos += sp.validSpacing;
    doc.text(`Valid Until: ${new Date(quote.validUntil).toLocaleDateString('en-GB')}`, margin, yPos);

    // Status badge - keep green for visual consistency
    if (quote.status === 'APPROVED') {
      doc.setFillColor(...SECONDARY_COLOR);
      doc.roundedRect(pageWidth - margin - 32, 40, 32, 9, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('APPROVED', pageWidth - margin - 16, 45.5, { align: 'center' });
    }

    // Customer Information Section - PRIMARY COLOR for header
    yPos += sp.customerGap;
    doc.setFillColor(...BACKGROUND_COLOR);
    doc.rect(margin, yPos, pageWidth - 2 * margin, sp.customerBarH, 'F');
    doc.setTextColor(...PRIMARY_TEXT);
    doc.setFontSize(sp.customerBarFontSize);
    doc.setFont('helvetica', 'bold');
    doc.text('CUSTOMER INFORMATION', margin + 5, yPos + sp.customerBarTextOffset);

    yPos += sp.customerBarSpacing;

    // Blank line above company name
    yPos += sp.blankAbove;

    // Company Name value
    doc.setTextColor(...PRIMARY_TEXT);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(quote.customerInfo.company || '', margin + 5, yPos);

    yPos += sp.nameSpacing;
    // Company Contact value (person name)
    doc.setTextColor(...PRIMARY_TEXT);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(quote.customerInfo.name || '', margin + 5, yPos);

    if (quote.customerInfo.address) {
      yPos += sp.contactSpacing;
      doc.setTextColor(...PRIMARY_TEXT);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(quote.customerInfo.address, margin + 5, yPos);
    }

    // Blank line below address
    yPos += sp.blankBelow;

    // Software Licenses Section - PRIMARY COLOR for header
    if (hasLicenses) {
      yPos += sp.sectionGap;
      doc.setFillColor(...BACKGROUND_COLOR);
      doc.rect(margin, yPos, pageWidth - 2 * margin, sp.sectionBarH, 'F');
      doc.setTextColor(...PRIMARY_TEXT);
      doc.setFontSize(sp.sectionBarFontSize);
      doc.setFont('helvetica', 'bold');
      doc.text('SOFTWARE LICENSES', margin + 5, yPos + sp.sectionBarTextOffset);

      yPos += sp.sectionBarSpacing;

      const licenseRows = quote.licenses.items.map((item) => [
        item.productName,
        item.quantity.toString(),
        formatCurrency(item.unitPrice),
        formatCurrency(item.monthlyAmount),
        formatCurrency(item.annualAmount),
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Product', 'Qty', 'Unit Price', 'Monthly', 'Annual']],
        body: licenseRows,
        foot: [[
          'Subtotal',
          '',
          '',
          formatCurrency(quote.licenses.monthlySubtotal),
          formatCurrency(quote.licenses.annualSubtotal),
        ]],
        theme: 'striped',
        headStyles: {
          fillColor: BACKGROUND_COLOR,
          textColor: PRIMARY_TEXT,
          fontSize: sp.tableHeadFontSize,
          fontStyle: 'bold',
          halign: 'left',
        },
        footStyles: {
          fillColor: BACKGROUND_COLOR,
          textColor: PRIMARY_TEXT,
          fontSize: sp.tableHeadFontSize,
          fontStyle: 'bold',
        },
        styles: {
          fontSize: sp.tableFontSize,
          textColor: PRIMARY_TEXT,
          cellPadding: sp.cellPadding,
          font: 'helvetica',
        },
        alternateRowStyles: {
          fillColor: [255, 255, 255],
        },
        margin: { left: margin, right: margin },
      });

      const finalY = (doc as any).lastAutoTable?.finalY || yPos + 50;
      yPos = finalY + sp.afterTableGap;
    }

    // Implementation Services Section - PRIMARY COLOR for header
    if (hasImplementation) {
      if (!singleSection && yPos > pageHeight - 80) {
        doc.addPage();
        addHeader(2);
        yPos = 42;
      }

      doc.setFillColor(...BACKGROUND_COLOR);
      doc.rect(margin, yPos, pageWidth - 2 * margin, sp.sectionBarH, 'F');
      doc.setTextColor(...PRIMARY_TEXT);
      doc.setFontSize(sp.sectionBarFontSize);
      doc.setFont('helvetica', 'bold');
      doc.text('IMPLEMENTATION SERVICES', margin + 5, yPos + sp.sectionBarTextOffset);

      yPos += sp.sectionBarSpacing;

      const implementationRows = quote.implementation.items.map((item) => [
        item.productName,
        item.quantity.toString(),
        formatCurrency(item.unitPrice),
        formatCurrency(item.annualAmount),
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Service', 'Qty', 'Unit Price', 'Total']],
        body: implementationRows,
        foot: [['Subtotal', '', '', formatCurrency(quote.implementation.annualSubtotal)]],
        theme: 'striped',
        headStyles: {
          fillColor: BACKGROUND_COLOR,
          textColor: PRIMARY_TEXT,
          fontSize: sp.tableHeadFontSize,
          fontStyle: 'bold',
          halign: 'left',
        },
        footStyles: {
          fillColor: BACKGROUND_COLOR,
          textColor: PRIMARY_TEXT,
          fontSize: sp.tableHeadFontSize,
          fontStyle: 'bold',
        },
        styles: {
          fontSize: sp.tableFontSize,
          textColor: PRIMARY_TEXT,
          cellPadding: sp.cellPadding,
          font: 'helvetica',
        },
        alternateRowStyles: {
          fillColor: [255, 255, 255],
        },
        margin: { left: margin, right: margin },
      });

      const finalY = (doc as any).lastAutoTable?.finalY || yPos + 50;
      yPos = finalY + sp.afterTableGap;
    }

    // Check if we need a new page for totals (only in multi-section mode)
    if (!singleSection && yPos > pageHeight - 60) {
      doc.addPage();
      addHeader(3);
      yPos = 42;
    }

    // Total Summary - PRIMARY COLOR for header
    doc.setFillColor(...BACKGROUND_COLOR);
    doc.rect(margin, yPos, pageWidth - 2 * margin, sp.totalBarH, 'F');
    doc.setTextColor(...PRIMARY_TEXT);
    doc.setFontSize(sp.totalBarFontSize);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL SUMMARY', margin + 5, yPos + sp.totalBarTextOffset);

    yPos += sp.totalTopGap;
    doc.setTextColor(...PRIMARY_TEXT);
    doc.setFontSize(10);

    const labelX = pageWidth / 2 + 10;
    const valueX = pageWidth - margin;

    // Line 1: Licenses Annual
    doc.setFont('helvetica', 'normal');
    doc.text('Licenses Annual:', labelX, yPos);
    doc.setFont('helvetica', 'bold');
    doc.text(formatCurrency(quote.licenses.annualSubtotal), valueX, yPos, { align: 'right' });

    yPos += sp.totalLineSpacing;

    // Line 2: Implementation Services
    doc.setFont('helvetica', 'normal');
    doc.text('Implementation Services:', labelX, yPos);
    doc.setFont('helvetica', 'bold');
    doc.text(formatCurrency(quote.implementation.annualSubtotal), valueX, yPos, { align: 'right' });

    yPos += sp.totalDividerSpacing;

    // Divider line
    doc.setDrawColor(...PRIMARY_TEXT);
    doc.setLineWidth(0.3);
    doc.line(labelX, yPos - 2, valueX, yPos - 2);

    // Line 3: TOTAL Y1
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL Y1:', labelX, yPos + sp.totalY1Offset);
    doc.setFontSize(13);
    doc.text(formatCurrency(quote.totalExVAT), valueX, yPos + sp.totalY1Offset, { align: 'right' });

    yPos += 8;

    // Notes Section - PRIMARY COLOR for header
    if (quote.notes) {
      yPos += sp.notesGap;
      
      if (!singleSection && yPos > pageHeight - 80) {
        doc.addPage();
        addHeader(4);
        yPos = 42;
      }

      doc.setFillColor(...BACKGROUND_COLOR);
      doc.rect(margin, yPos, pageWidth - 2 * margin, sp.notesBarH, 'F');
      doc.setTextColor(...PRIMARY_TEXT);
      doc.setFontSize(sp.notesBarFontSize);
      doc.setFont('helvetica', 'bold');
      doc.text('NOTES & TERMS', margin + 5, yPos + sp.notesBarTextOffset);

      yPos += sp.notesBarSpacing;
      doc.setTextColor(...PRIMARY_TEXT);
      doc.setFontSize(sp.notesFontSize);
      doc.setFont('helvetica', 'normal');

      const notesLines = doc.splitTextToSize(quote.notes, pageWidth - 2 * margin - 10);
      doc.text(notesLines, margin + 5, yPos);
    }

    // Footer on last page - PRIMARY COLOR
    const addFooter = () => {
      const footerY = pageHeight - 20;
      doc.setFontSize(8);
      doc.setTextColor(...TEXT_LIGHT);
      doc.setFont('helvetica', 'normal');
      doc.text('EVOQIA - Your Digital Agency', pageWidth / 2, footerY, { align: 'center' });
      doc.setTextColor(...PRIMARY_TEXT); // PRIMARY COLOR instead of SECONDARY
      doc.text('sales@evoqia.eu | www.evoqia.eu', pageWidth / 2, footerY + 4, { align: 'center' });
    };

    addFooter();

    // Save the PDF
    const filename = `Quote_${quote.referenceCode}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);

    console.log('PDF generated successfully with condensed layout and primary color scheme:', filename);
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
};